import type { InventoryItem } from "../api/types";
import { useCan } from "../auth/AuthProvider";
import { CAP } from "../auth/capabilities";
import { formatDateTime } from "../components/ui";
import {
  AVAILABILITY_LABELS,
  FAMILY_LABELS,
  itemLabel,
  thresholdLabel,
  variantLabel,
} from "./labels";
import { MovementHistoryPanel } from "./MovementHistoryPanel";

/**
 * One page of stock, one row per sellable thing.
 *
 * NOT one row per product. A frame with six colours has six rows because six
 * things sit on six shelves; a cosmetic lens has one; an accessory that comes
 * one way has one AND IS NOT GIVEN A COLOUR — no swatch, no "Default", because
 * inventing one would put a choice in front of an operator that the shop does
 * not offer and a shopper is never asked to make.
 *
 * Availability is the backend's word, never recomputed here. A second opinion
 * about what "low" means is how two screens come to disagree about one number.
 *
 * History expands in place rather than on a detail route. Checking why a count
 * looks wrong is something an operator does while scanning the list, and
 * sending them to another screen and back loses their place in it.
 */
export function InventoryTable({
  items,
  openHistoryFor,
  onToggleHistory,
  onAdjust,
  onSetThreshold,
}: {
  items: InventoryItem[];
  openHistoryFor: string | null;
  onToggleHistory: (itemId: string) => void;
  onAdjust: (item: InventoryItem) => void;
  onSetThreshold: (item: InventoryItem) => void;
}) {
  return (
    <div className="inventory__scroll">
      <table className="table">
        <thead>
          <tr>
            <th scope="col">Product</th>
            <th scope="col">Variant</th>
            <th scope="col">Family</th>
            <th scope="col" className="num">
              Stock
            </th>
            <th scope="col">Availability</th>
            <th scope="col">Low-stock threshold</th>
            <th scope="col">Last movement</th>
            <th scope="col">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <InventoryRow
              key={item.inventory_item_id}
              item={item}
              historyOpen={openHistoryFor === item.inventory_item_id}
              onToggleHistory={onToggleHistory}
              onAdjust={onAdjust}
              onSetThreshold={onSetThreshold}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InventoryRow({
  item,
  historyOpen,
  onToggleHistory,
  onAdjust,
  onSetThreshold,
}: {
  item: InventoryItem;
  historyOpen: boolean;
  onToggleHistory: (itemId: string) => void;
  onAdjust: (item: InventoryItem) => void;
  onSetThreshold: (item: InventoryItem) => void;
}) {
  const canAdjust = useCan(CAP.inventoryAdjust);
  // Both from the one helper, so this row and the dialogs it opens cannot
  // disagree about whether there is a variant to show. `null` is an accessory
  // that comes one way.
  const variant = variantLabel(item);
  const label = itemLabel(item);

  return (
    <>
      <tr>
        <td>
          <span className="inventory__product">{item.product_name}</span>
          {/* "Code missing" rather than the slug: the slug is a URL path, and
              substituting it would look like a shelf code that is not one. */}
          <span className="inventory__code">
            {item.product_code ?? "Code missing"}
          </span>
        </td>

        <td>
          {variant === null ? (
            /* Nothing, said out loud. The variant behind this row is real and
               every action still uses it, but it is a place for stock to live
               rather than a decision anyone made, so printing the name it was
               given would offer an operator a choice the shop does not sell.
               A dash where a name would be — this is a table, and a column
               cannot simply stop — and a word for a reader who cannot see it. */
            <span className="inventory__variant muted">
              <span aria-hidden="true">—</span>
              <span className="sr-only">No variant</span>
            </span>
          ) : (
            <span className="inventory__variant">
              {/* A swatch only where the variant really has a colour. A null
                  one is an accessory that comes one way, and drawing it as
                  black would show a colour the shop does not sell. */}
              {item.swatch_hex ? (
                <span
                  className="inventory__swatch"
                  style={{ background: item.swatch_hex }}
                  aria-hidden="true"
                />
              ) : null}
              {variant}
            </span>
          )}
          {item.size_options.length > 0 ? (
            <span className="inventory__sizes">
              {item.size_options.join(" · ")}
            </span>
          ) : null}
        </td>

        <td className="muted">
          {item.family ? FAMILY_LABELS[item.family] : "Unclassified"}
        </td>

        <td className="num inventory__stock">{item.stock_quantity}</td>

        <td>
          <span className={`badge badge--${item.availability}`}>
            {AVAILABILITY_LABELS[item.availability]}
          </span>
        </td>

        <td className="muted">{thresholdLabel(item.low_stock_threshold)}</td>

        <td className="muted">{formatDateTime(item.last_movement_at)}</td>

        <td>
          <div className="inventory__actions">
            {/* Text actions, never icons alone: an operator must be able to
                read what a control does before pressing it. */}
            <button
              type="button"
              className="btn btn--sm"
              aria-expanded={historyOpen}
              onClick={() => onToggleHistory(item.inventory_item_id)}
            >
              {historyOpen ? "Hide history" : "View history"}
            </button>
            {/* Two conditions, and they are different things. `adjustable` is
                the ITEM's answer — an archived product's stock is a record, not
                a shelf. The capability is the ADMINISTRATOR's, and an operator
                who may only read stock is not offered a control that would be
                refused. */}
            {item.adjustable && canAdjust ? (
              <>
                <button
                  type="button"
                  className="btn btn--sm btn--primary"
                  aria-label={`Adjust stock for ${label}`}
                  onClick={() => onAdjust(item)}
                >
                  Adjust stock
                </button>
                <button
                  type="button"
                  className="btn btn--sm btn--subtle"
                  aria-label={`Set low-stock threshold for ${label}`}
                  onClick={() => onSetThreshold(item)}
                >
                  Set threshold
                </button>
              </>
            ) : item.adjustable ? null : (
              // Archived: the record stays readable and nothing may be added
              // to it. Said out loud rather than shown as three missing
              // buttons, which reads as a screen that failed to load.
              <span className="muted" data-testid="inventory-read-only">
                Archived — read only
              </span>
            )}
          </div>
        </td>
      </tr>

      {historyOpen ? (
        <tr className="inventory__historyRow">
          <td colSpan={8}>
            <MovementHistoryPanel
              itemId={item.inventory_item_id}
              itemLabel={label}
            />
          </td>
        </tr>
      ) : null}
    </>
  );
}
